import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { authedApiFetch } from '../../utils/Api';
import PendingFilesList from './PendingFilesList';
import AscentUpload from './AscentUpload';
import FileUploadTitle from './FileUploadTitle';

import MappingReview from './MappingReview';
import { useLoaderData, useSearchParams, useOutletContext } from 'react-router';
import { useEffect, useState } from 'react';
import FileViewer from './FileViewer';

import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	Typography,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SyncIcon from '@mui/icons-material/Sync';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';

// See BE app/models/ascent_file.rb for source of possible status values
const FileStatus = {
	UPLOADED: 'uploaded',
	PROCESSING: 'processing',
	PROCESSED: 'processed',
	MAPPING_READY: 'mapping_ready',
	MAPPING_APPROVED: 'mapping_approved',
	FAILED: 'failed',
	SYNCED: 'synced',
};

export const clientLoader = async ({ params }) => {
	const { tenantId } = params;

	const files = await authedApiFetch({
		endpoint: `/tenants/${tenantId}/ascent_files`,
		method: 'GET',
	});

	return {
		tenantId,
		uploadedFiles: files.map((file) => ({
			id: file.id,
			fileName: file.original_file_name,
			object: file.target_object,
			dateCreated: new Date(file.created_at).toISOString().split('T')[0],
			owner: file.user?.full_name || file.user_id,
			status: file.status,
			mappingConfidence: file.metadata?.mapping_confidence || null,
			databricks_table_name: file.databricks_table_name,
			userId: file.user_id,
			userRole: file.user?.role,
		})),
	};
};

const AscentFiles = () => {
	// React Router hooks
	const { tenantId, uploadedFiles: initialFiles } = useLoaderData();
	let [_searchParams, setSearchParams] = useSearchParams();

	// State management
	const [processingFile, _setProcessingFile] = useState(null);
	const [mappingReview, setMappingReview] = useState(null);
	const [viewingFile, setViewingFile] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFiles, setUploadedFiles] = useState(initialFiles);
	// Track files that have been uploaded but not yet appeared in the table
	const [pendingFiles, setPendingFiles] = useState([]);
	// Track files that are in the table but still processing
	const [processingFileIds, setProcessingFileIds] = useState(new Set());

	const { showNotification, user } = useOutletContext();

	// Deletable statuses
	const DELETABLE_STATUSES = ['failed', 'mapping_ready', 'mapping_approved'];

	// Check if current user can delete a file
	const canDeleteFile = (file) => {
		if (!DELETABLE_STATUSES.includes(file.status)) {
			return false;
		}

		if (user?.role === 'tenant_read_only') {
			return false;
		}

		if (
			user?.role === 'altvia_admin' ||
			user?.role === 'altvia_power_user' ||
			user?.role === 'altvia_user' ||
			user?.role === 'tenant_admin'
		) {
			return true;
		}

		// Tenant users can only delete their own files
		return user?.id === file.userId;
	};

	// required b/c we are modifying the uploadedFiles in-memory
	useEffect(() => {
		setUploadedFiles(initialFiles);
	}, [initialFiles]);

	// Set up polling when files are uploading or processing
	useEffect(() => {
		if (pendingFiles.length === 0 && processingFileIds.size === 0) return;

		// Check for updates every 5 seconds
		// This also means the visible counter updates every 5 seconds. this is deliberate
		const interval = setInterval(() => {
			refreshFileList();
		}, 5000);

		// Clean up on unmount
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingFiles.length, processingFileIds.size, tenantId]);

	const handleFileUpload = async (file, schema) => {
		// Add to pending files
		setPendingFiles((prev) => [
			...prev,
			{
				fileName: file.name,
				object: schema.object,
				uploadStartTime: new Date(),
			},
		]);

		const reader = new FileReader();

		reader.onload = async (e) => {
			const fileContents = e.target.result;
			const base64File = btoa(
				new Uint8Array(fileContents).reduce(
					(data, byte) => data + String.fromCharCode(byte),
					''
				)
			);

			try {
				showNotification(`Uploading ${file.name}...`, 'info');

				await authedApiFetch({
					endpoint: `/tenants/${tenantId}/ascent_files`,
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					payload: {
						file_name: file.name,
						file_contents: base64File,
						target_object: schema.object,
					},
				});

				showNotification(
					`${file.name} uploaded successfully, processing in progress...`,
					'success'
				);

				// Start polling to check when the file appears in the list
				refreshFileList();
			} catch (error) {
				// Remove from pending files on error
				setPendingFiles((prev) => prev.filter((f) => f.fileName !== file.name));

				showNotification(`Error uploading ${file.name}`, 'error');
				console.error('Error:', error);
			}
		};

		reader.readAsArrayBuffer(file);
	};

	const handleViewFile = (file) => {
		setViewingFile(file);
	};

	const handleReviewMapping = async (file) => {
		setSelectedFile(file);
		setMappingReview(true);
	};

	const handleBackToList = () => {
		setMappingReview(null);
		setSelectedFile(null);
		setViewingFile(null);

		// Reset search params, which might be modified by DataGrid on viewing page
		setSearchParams({});
	};

	const handleSync = async (row) => {
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/ascent_files/${row.id}/sync`,
				method: 'POST',
			});

			showNotification('Successfully started sync', 'success');
		} catch (e) {
			console.error('Error when attempting to start sync:', e.message);
			showNotification('Failed to start sync due to error', 'error');
		}
	};

	const refreshFileList = async () => {
		try {
			const updatedFiles = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/ascent_files`,
				method: 'GET',
			});

			const formattedFiles = updatedFiles.map((file) => ({
				id: file.id,
				fileName: file.original_file_name,
				object: file.target_object || 'Detecting...',
				dateCreated: new Date(file.created_at).toISOString().split('T')[0],
				owner: file.user_id,
				status: file.status,
				mappingConfidence: file.metadata?.mapping_confidence || null,
				databricks_table_name: file.databricks_table_name,
				userId: file.user_id,
				userRole: file.user?.role,
			}));

			// Check for pending files that have now appeared in the list
			const newPendingFiles = [...pendingFiles];
			const pendingFilesFound = [];

			for (let i = 0; i < newPendingFiles.length; i++) {
				const pendingFile = newPendingFiles[i];
				const foundFile = formattedFiles.find((file) => {
					// Check if the file name matches or starts with the pending file name (accounting for backend renaming)
					const fileNameMatches =
						file.fileName === pendingFile.fileName ||
						file.fileName.startsWith(
							pendingFile.fileName.replace(/\.[^/.]+$/, '')
						);
					// Check if file was created after upload started
					const fileCreatedAfterUpload =
						new Date(file.dateCreated) >= pendingFile.uploadStartTime;
					return fileNameMatches && fileCreatedAfterUpload;
				});

				if (foundFile) {
					pendingFilesFound.push({ file: foundFile, index: i });

					// Add to processing IDs if still processing
					if (
						foundFile.status === 'uploaded' ||
						foundFile.status === 'processing'
					) {
						setProcessingFileIds((prev) => new Set([...prev, foundFile.id]));
					}

					// Show notification when file appears in table
					showNotification(`${foundFile.fileName} is being processed`, 'info');
				}
			}

			// Remove found files from pending list (in reverse order to avoid index issues)
			pendingFilesFound.sort((a, b) => b.index - a.index);
			for (const { index } of pendingFilesFound) {
				newPendingFiles.splice(index, 1);
			}

			setPendingFiles(newPendingFiles);

			// Update processing IDs for existing files
			const newProcessingFileIds = new Set(processingFileIds);
			formattedFiles.forEach((file) => {
				if (processingFileIds.has(file.id)) {
					if (file.status !== 'uploaded' && file.status !== 'processing') {
						newProcessingFileIds.delete(file.id);

						// Show notification if file is now ready for mapping
						if (file.status === 'mapping_ready') {
							showNotification(
								`${file.fileName} is ready for mapping`,
								'success'
							);
						} else if (file.status === 'failed') {
							showNotification(
								`Processing failed for ${file.fileName}`,
								'error'
							);
						}
					}
				}
			});

			setProcessingFileIds(newProcessingFileIds);
			setUploadedFiles(formattedFiles);
		} catch (error) {
			console.error('Error refreshing file list:', error);
		}
	};

	const handleDeletion = async (fileId, fileName) => {
		try {
			showNotification(`Deleting '${fileName}'...`, 'info');

			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/ascent_files/${fileId}`,
				method: 'DELETE',
			});

			const updatedFiles = uploadedFiles.filter((f) => f.id !== fileId);
			setUploadedFiles(updatedFiles);
			showNotification(`'${fileName}' is deleted.`, 'success');
		} catch (e) {
			showNotification(`Failed to delete '${fileName}'.`, 'error');
		}
	};

	// Columns to display in MUI DataGrid
	const columns = [
		{
			field: 'fileName',
			headerName: 'File Name',
			minWidth: 400,
			flex: 2,
			renderCell: (params) => (
				<Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre' }}>
					{(processingFile?.id === params.row.id ||
						processingFileIds.has(params.row.id) ||
						params.row.status === 'uploaded' ||
						params.row.status === 'processing') && (
						<CircularProgress size={16} sx={{ mr: 1 }} />
					)}
					{params.value}
				</Box>
			),
		},

		{ field: 'object', headerName: 'Object', minWidth: 200, flex: 1 },
		{
			field: 'dateCreated',
			headerName: 'Date Created',
			minWidth: 150,
			flex: 1,
		},
		{ field: 'owner', headerName: 'Owner', minWidth: 150, flex: 1 },
		{
			field: 'status',
			headerName: 'Status',
			minWidth: 250,
			flex: 1,
			renderCell: (params) => (
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{params.row.mappingConfidence && (
						<Typography variant='caption' sx={{ mr: 1 }}>
							({Math.round(params.row.mappingConfidence * 100)}% match)
						</Typography>
					)}
					{params.value}
				</Box>
			),
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			minWidth: 100,
			flex: 0.5,
			getActions: (params) => [
				<GridActionsCellItem
					key={`view-${params.row.id}`}
					icon={<VisibilityIcon />}
					onClick={() => handleViewFile(params.row)}
					label='View'
					disabled={params.row.status === 'failed'}
				/>,
				<GridActionsCellItem
					key={`review-mapping-${params.row.id}`}
					icon={<AssignmentIcon />}
					onClick={() => handleReviewMapping(params.row)}
					label='Review Mapping'
					showInMenu
					disabled={
						params.row.status !== FileStatus.MAPPING_READY &&
						params.row.status !== FileStatus.MAPPING_APPROVED
					}
				/>,
				<GridActionsCellItem
					key={`sync-${params.row.id}`}
					icon={<SyncIcon />}
					onClick={() => handleSync(params.row)}
					label='Sync'
					showInMenu
					disabled={params.row.status !== FileStatus.MAPPING_APPROVED}
				/>,
				<GridActionsCellItem
					key={`delete-${params.row.id}`}
					icon={<DeleteIcon />}
					onClick={() => handleDeletion(params.row.id, params.row.fileName)}
					showInMenu
					label='Delete' //when we have more actions, we can add 'showInMenu' attribute
					disabled={!canDeleteFile(params.row)}
				/>,
			],
		},
	];

	// Switch rendered components based on state
	if (mappingReview && selectedFile) {
		return (
			<Dialog fullScreen open>
				<Box sx={{ p: 3 }}>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={handleBackToList}
						sx={{ mb: 2 }}
					>
						Back to File List
					</Button>
					<MappingReview
						fileName={selectedFile.fileName}
						fileId={selectedFile.id}
						tenantId={tenantId}
						showNotification={showNotification}
						onClose={handleBackToList}
						approved={selectedFile.status == FileStatus.MAPPING_APPROVED}
					/>
				</Box>
			</Dialog>
		);
	} else if (viewingFile) {
		return (
			<Dialog fullScreen open>
				<Box
					height='100vh' // absolute height required for MUI DataGrid to autosize
					sx={{
						display: 'flex',
						flexDirection: 'column',
						rowGap: 1,
						p: 3,
					}}
				>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={handleBackToList}
						sx={{ width: 'fit-content', mb: 2 }}
					>
						Back to File List
					</Button>
					<FileViewer
						fileName={viewingFile.fileName}
						fileId={viewingFile.id}
						tenantId={tenantId}
					/>
				</Box>
			</Dialog>
		);
	} else {
		return (
			<Box
				sx={{
					py: 0,
					pt: 1.5,
					width: '100%',
					overflow: 'auto',
				}}
			>
				<FileUploadTitle />
				<Typography variant='body1' sx={{ mb: 3 }}>
					Use our streamlined file upload to rapidly import CSV data into any
					Altvia object. Whether importing investor records, fund data, or other
					structured information, the File Upload feature provides a fast and
					reliable way to load your existing data into the system. Define
					mappings, validate your data, and quickly integrate your information
					with Altvia&apos;s powerful data management capabilities.
				</Typography>

				<AscentUpload onUpload={handleFileUpload} />
				{/* Show pending files that haven't appeared in the table yet */}
				<PendingFilesList pendingFiles={pendingFiles} />

				<DataGrid
					rows={uploadedFiles}
					columns={columns}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					pageSizeOptions={[5, 10, 25]}
					sx={{ mt: 3, width: '100%', height: 'auto' }}
					components={{
						MoreActionsIcon: MoreVertIcon,
					}}
				/>
			</Box>
		);
	}
};

export default AscentFiles;
